import React from "react";
import TopoTexture from "@/components/TopoTexture";
import { motion } from "framer-motion";
import { Phone, Star, ShieldCheck, Clock, Award } from "lucide-react";
import { COMPANY } from "@/data/site";
import InstantQuoteWizard from "@/components/InstantQuoteWizard";

const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
      <TopoTexture variant="light" opacity={0.05} />
      {/* Decorative Wyoming mountain silhouette behind */}
      <div className="absolute top-24 right-0 -z-10 w-1/2 h-[60%] opacity-[0.08] pointer-events-none">
        <svg viewBox="0 0 600 400" className="w-full h-full text-[var(--ct-primary)]">
          <polygon fill="currentColor" points="0,400 120,180 220,280 340,80 460,260 600,160 600,400" />
        </svg>
      </div>

      <div className="section-container relative grid lg:grid-cols-12 gap-10 items-center">
        {/* Left - Copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ct-accent)] text-[var(--ct-primary)] text-xs font-semibold tracking-widest uppercase mb-6">
              <Award className="w-3.5 h-3.5" />
              Serving Wyoming Since 1980
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold leading-[1.05] text-[var(--ct-text)]"
              style={{ fontFamily: "Outfit" }}
            >
              Trusted <span className="text-[var(--ct-primary)]">Movers in Casper, WY</span>
              <br />
              <span className="text-[var(--ct-primary)]">Since 1980.</span>
            </h1>

            <p className="mt-6 text-lg text-[var(--ct-text-muted)] leading-relaxed max-w-2xl">
              Professional residential and commercial moving services in Casper, Mills, and
              surrounding Wyoming areas. Stress-free moves handled with care, precision, and
              over {COMPANY.yearsInBusiness} years of local experience.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#quote" className="btn-primary" data-testid="hero-quote-btn">
                Get a Free Quote
              </a>
              <a href={`tel:${COMPANY.phoneTel}`} className="btn-outline" data-testid="hero-call-btn">
                <Phone className="w-4 h-4" /> Call {COMPANY.phone}
              </a>
            </div>

            {/* Trust bar */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="trust-bar">
              <TrustItem icon={<Award className="w-4 h-4" />} label="Since 1980" />
              <TrustItem
                icon={<Star className="w-4 h-4 fill-current" />}
                label={`${COMPANY.rating}★ · ${COMPANY.reviews} Reviews`}
              />
              <TrustItem icon={<Clock className="w-4 h-4" />} label="Open 24 Hours" />
              <TrustItem icon={<ShieldCheck className="w-4 h-4" />} label="Fully Insured" />
            </div>
          </motion.div>
        </div>

        {/* Right - Instant Quote Wizard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <InstantQuoteWizard />

          {/* Floating stat card */}
          <div className="hidden md:flex absolute -bottom-6 -left-6 bg-[var(--ct-primary)] text-white rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.2)] p-4 items-center gap-3 border border-[var(--ct-primary)]">
            <div className="w-10 h-10 rounded-full bg-[var(--ct-secondary)] flex items-center justify-center">
              <Award className="w-5 h-5 text-[var(--ct-primary)]" />
            </div>
            <div>
              <div className="text-xl font-bold text-[var(--ct-secondary)]" style={{ fontFamily: "Outfit" }}>{COMPANY.yearsInBusiness}+ yrs</div>
              <div className="text-[10px] uppercase tracking-widest text-white/80">Serving Wyoming</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const TrustItem = ({ icon, label }) => (
  <div className="flex items-center gap-3 text-[var(--ct-primary)]">
    <span className="w-9 h-9 rounded-full bg-[var(--ct-primary)] text-[var(--ct-secondary)] flex items-center justify-center flex-shrink-0">{icon}</span>
    <span className="text-sm font-semibold">{label}</span>
  </div>
);

export default Hero;
